import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { Col, Actor, Director, ImagePreset } from '@vodafoneis/sjonvarpskjarni-js-lib';
import type { Movie, Series } from '@vodafoneis/sjonvarpskjarni-js-lib';
import useContentGridRenderer from '../hooks/useContentGridRenderer';
import { ImageComponent, ImageComponentContainer } from './ImageComponent';
import { FONT_SECONDARY } from '../styles/colors';
import { ellipsis } from '../styles/ellipsis';
import { useSearchString } from '../hooks/useSearchString';

const SimilarContentContainer = styled.div`
	margin-top: 20px;
	display: block;

	> .row > div {
		margin-bottom: 20px;
	}
`;

const ContributorsTitle = styled.div`
	font-weight: bold;
	font-size: 20px;
	text-transform: uppercase;
	margin-bottom: 10px;
`;

const ContributorContainer = styled.div`
	display: flex;
	margin-bottom: 15px;
	cursor: pointer;
`;

const ContributorImageContainer = styled(ImageComponentContainer)`
	width: 60px;
	height: 60px;
	min-width: 60px;
	min-height: 60px;
	border-radius: 50%;
	margin-right: 20px;
	position: relative;
	overflow: hidden;
`;

const ContributorImage = styled(ImageComponent)`
	width: 100%;
	height: 100%;
`;

const ContributorTitleContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	overflow: hidden;
`;

const ContributorTitle = styled.div`
	font-weight: bold;
	font-size: 20px;
	line-height: 1.3;
	${ellipsis};
`;

const ContributorDescription = styled.div`
	font-size: 18px;
	line-height: 1.3;
	color: ${FONT_SECONDARY};
	${ellipsis};
`;

const ContributorComponent = ({ contributor }: { contributor: Director | Actor }) => {
	const router = useRouter();
	const { setSearchString } = useSearchString();

	const onClick = useCallback(
		(e) => {
			e.preventDefault();
			setSearchString(contributor.name);
			router.push('/search');
		},
		[contributor, router, setSearchString]
	);

	return (
		<ContributorContainer onClick={onClick}>
			<ContributorImageContainer>
				<ContributorImage images={[contributor.getImageUrl({ presets: [ImagePreset.CREDIT_SMALL] })]} />
			</ContributorImageContainer>
			<ContributorTitleContainer>
				<ContributorTitle>{contributor.name}</ContributorTitle>
				{contributor instanceof Actor && <ContributorDescription>{contributor.character}</ContributorDescription>}
			</ContributorTitleContainer>
		</ContributorContainer>
	);
};

const SimilarContentDirectors = ({ directors }) => {
	const { t } = useTranslation();

	if (!directors || directors.length === 0) return null;

	return (
		<>
			<ContributorsTitle>{t('ContentInfo.Director', { count: directors.length })}</ContributorsTitle>
			<div className={'row'}>
				{directors.map((director) => (
					<Col key={director.id} xs={12} lg={directors.length >= 2 ? 6 : 12}>
						<ContributorComponent contributor={director} />
					</Col>
				))}
			</div>
		</>
	);
};

const SimilarContentActors = ({ actors }) => {
	const { t } = useTranslation();

	if (!actors || actors.length === 0) return null;

	return (
		<>
			<ContributorsTitle>{t('ContentInfo.Actor', { count: actors.length })}</ContributorsTitle>
			<div className={'row'}>
				{actors.map((actor) => (
					<Col key={actor.id} xs={12} lg={actors.length > 7 ? 6 : 12}>
						<ContributorComponent contributor={actor} />
					</Col>
				))}
			</div>
		</>
	);
};

export const SimilarContent: React.FC<{ content: Movie | Series }> = ({ content }) => {
	const { t } = useTranslation();

	const renderItem = useContentGridRenderer({
		columnConfig: {
			xs: 4,
			md: 4,
		},
	});

	const itemComponents = useMemo(() => {
		if (!content?.similar) return null;

		return content.similar.map((c) => renderItem(c, true));
	}, [content, renderItem]);

	if (!content?.similar) return null;

	return (
		<SimilarContentContainer>
			<div className={'row'}>
				<Col xs={12} md={6} className={'order-2 order-md-1'}>
					<SimilarContentDirectors directors={content?.directors} />
					<SimilarContentActors actors={content?.actors} />
				</Col>
				<Col xs={12} md={6} className={'order-1 order-md-2'}>
					<ContributorsTitle>{t('ContentInfo.SimilarContent')}</ContributorsTitle>
					<div className={'row'}>{itemComponents}</div>
				</Col>
			</div>
		</SimilarContentContainer>
	);
};
